/**
 * FutureBotChat — the conversation surface inside the FutureBot panel.
 * Message list (markdown AI replies, tool-activity chips, typing indicator),
 * quick-start suggestion chips, and the composer.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Textarea,
  IconButton,
  HStack,
  VStack,
  Spinner,
  Icon,
  Image,
  Button,
  Progress,
  useColorModeValue,
  useToast,
  keyframes,
} from '@chakra-ui/react';
import {
  FiArrowUp,
  FiCheck,
  FiAlertTriangle,
  FiZap,
  FiSquare,
  FiPaperclip,
  FiFileText,
  FiX,
  FiMic,
  FiVolume2,
} from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import astronautIcon from '../../assets/img/astronaut-icon-chat-bubble.png';
import { useFutureBotAttachments } from './useFutureBotAttachments';
import { useSpeechDictation } from './useSpeechDictation';

const pulse = keyframes`
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
  40% { transform: scale(1); opacity: 1; }
`;

const micPulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.45); }
  70% { box-shadow: 0 0 0 9px rgba(229, 62, 62, 0); }
  100% { box-shadow: 0 0 0 0 rgba(229, 62, 62, 0); }
`;

function TypingIndicator() {
  const dotBg = useColorModeValue('gray.400', 'gray.500');
  return (
    <HStack spacing={1.5} px={4} py={3}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          w="7px"
          h="7px"
          borderRadius="full"
          bg={dotBg}
          animation={`${pulse} 1.2s ease-in-out ${i * 0.18}s infinite`}
        />
      ))}
    </HStack>
  );
}

function ActivityChip({ message }) {
  const chipBg = useColorModeValue('purple.50', 'whiteAlpha.100');
  const chipBorder = useColorModeValue('purple.100', 'whiteAlpha.200');
  const chipColor = useColorModeValue('purple.700', 'purple.200');
  const doneColor = useColorModeValue('green.500', 'green.300');
  const errorColor = useColorModeValue('orange.500', 'orange.300');

  return (
    <Flex justify="center" w="100%">
      <HStack
        spacing={2}
        px={3}
        py={1.5}
        bg={chipBg}
        border="1px solid"
        borderColor={chipBorder}
        borderRadius="full"
        maxW="90%"
      >
        {message.status === 'running' ? (
          <Spinner size="xs" color={chipColor} />
        ) : message.status === 'error' ? (
          <Icon as={FiAlertTriangle} boxSize={3} color={errorColor} />
        ) : (
          <Icon as={FiCheck} boxSize={3} color={doneColor} />
        )}
        <Text fontSize="xs" fontWeight="medium" color={chipColor} noOfLines={1}>
          {message.text}
        </Text>
      </HStack>
    </Flex>
  );
}

/** Attachment inside a sent user bubble: image thumbnail or file chip. */
function SentAttachment({ att }) {
  const [imageFailed, setImageFailed] = useState(false);
  const isImage = (att.mimeType || '').startsWith('image/') && att.downloadURL && !imageFailed;

  if (isImage) {
    return (
      <Image
        src={att.downloadURL}
        alt={att.name}
        maxH="140px"
        maxW="100%"
        borderRadius="lg"
        onError={() => setImageFailed(true)}
      />
    );
  }
  return (
    <HStack spacing={2} px={2.5} py={1.5} bg="whiteAlpha.300" borderRadius="lg" maxW="100%">
      <Icon as={FiFileText} boxSize={4} flexShrink={0} />
      <Text fontSize="xs" fontWeight="medium" noOfLines={1}>
        {att.name}
      </Text>
    </HStack>
  );
}

/** Pending attachment chip in the composer (uploading or ready to send). */
function PendingAttachmentChip({ att, onRemove }) {
  const chipBg = useColorModeValue('gray.100', 'gray.700');
  const chipBorder = useColorModeValue('gray.200', 'gray.600');
  const isImage = (att.mimeType || '').startsWith('image/');

  return (
    <HStack
      spacing={2}
      pl={att.status === 'done' && isImage && att.downloadURL ? 1 : 2}
      pr={1}
      py={1}
      bg={chipBg}
      border="1px solid"
      borderColor={chipBorder}
      borderRadius="lg"
      maxW="100%"
    >
      {att.status === 'done' && isImage && att.downloadURL ? (
        <Image src={att.downloadURL} alt={att.name} boxSize="28px" objectFit="cover" borderRadius="md" />
      ) : (
        <Icon as={FiFileText} boxSize={3.5} flexShrink={0} />
      )}
      <Box minW={0} maxW="140px">
        <Text fontSize="xs" fontWeight="medium" noOfLines={1}>
          {att.name}
        </Text>
        {att.status === 'uploading' && (
          <Progress value={att.progress} size="xs" colorScheme="purple" borderRadius="full" mt={0.5} />
        )}
      </Box>
      <IconButton
        icon={<FiX />}
        aria-label={`Remove ${att.name}`}
        size="xs"
        variant="ghost"
        borderRadius="full"
        onClick={onRemove}
      />
    </HStack>
  );
}

function MessageBubble({ message, onSpeak }) {
  const isUser = message.type === 'user';
  const isError = message.type === 'error';
  const userBg = useColorModeValue('blue.500', 'blue.400');
  const aiBg = useColorModeValue('gray.100', 'gray.700');
  const errorBg = useColorModeValue('orange.50', 'rgba(251,146,60,0.15)');
  const errorBorder = useColorModeValue('orange.200', 'orange.600');
  const aiColor = useColorModeValue('gray.800', 'gray.100');
  const codeBg = useColorModeValue('blackAlpha.100', 'whiteAlpha.200');

  if (isUser) {
    const attachments = Array.isArray(message.attachments) ? message.attachments : [];
    return (
      <Flex justify="flex-end" w="100%">
        <Box
          bg={userBg}
          color="white"
          px={4}
          py={2.5}
          borderRadius="2xl"
          borderBottomRightRadius="md"
          maxW="82%"
          fontSize="sm"
          whiteSpace="pre-wrap"
          wordBreak="break-word"
        >
          {attachments.length > 0 && (
            <VStack align="flex-end" spacing={1.5} mb={message.text ? 2 : 0}>
              {attachments.map((att, i) => (
                <SentAttachment key={att.gsUri || i} att={att} />
              ))}
            </VStack>
          )}
          {message.text}
        </Box>
      </Flex>
    );
  }

  return (
    <Flex justify="flex-start" w="100%" gap={2} align="flex-end">
      <Image src={astronautIcon} boxSize="24px" borderRadius="full" alt="FutureBot" flexShrink={0} mb={1} />
      <Box
        bg={isError ? errorBg : aiBg}
        color={aiColor}
        px={4}
        py={2.5}
        borderRadius="2xl"
        borderBottomLeftRadius="md"
        maxW="85%"
        fontSize="sm"
        border={isError ? '1px solid' : 'none'}
        borderColor={isError ? errorBorder : 'transparent'}
        sx={{
          p: { marginBottom: '0.5em' },
          'p:last-child': { marginBottom: 0 },
          'ul, ol': { paddingLeft: '1.25em', marginBottom: '0.5em' },
          li: { marginBottom: '0.15em' },
          a: { color: 'blue.400', textDecoration: 'underline' },
          code: { bg: codeBg, px: '4px', py: '1px', borderRadius: 'md', fontSize: '0.85em' },
          pre: { bg: codeBg, p: 2, borderRadius: 'md', overflowX: 'auto', marginBottom: '0.5em' },
          'h1, h2, h3': { fontWeight: 'bold', marginBottom: '0.35em', fontSize: 'md' },
          table: { width: '100%', marginBottom: '0.5em' },
          'th, td': { border: '1px solid', borderColor: 'inherit', px: 2, py: 1, fontSize: 'xs' },
          blockquote: { borderLeft: '3px solid', borderColor: 'gray.400', pl: 3, opacity: 0.9 },
        }}
      >
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text || ''}</ReactMarkdown>
      </Box>
      {!isError && onSpeak && message.text && (
        <IconButton
          icon={<FiVolume2 />}
          aria-label="Read this reply aloud"
          size="xs"
          variant="ghost"
          borderRadius="full"
          opacity={0.4}
          _hover={{ opacity: 1 }}
          flexShrink={0}
          mb={1}
          onClick={() => onSpeak(message.text)}
        />
      )}
    </Flex>
  );
}

function dragHasFiles(e) {
  return Array.from(e.dataTransfer?.types || []).includes('Files');
}

export default function FutureBotChat({
  messages,
  isResponding,
  onSend,
  onStop,
  suggestions,
  pageTitle,
  sessionId,
  onSpeak,
}) {
  const [draft, setDraft] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const toast = useToast();

  const { attachments, isUploading, addFiles, removeAttachment, clearAttachments } =
    useFutureBotAttachments(sessionId);

  const dictation = useSpeechDictation({
    onFinal: (chunk) => {
      setDraft((prev) => (prev ? `${prev.replace(/\s+$/, '')} ${chunk}` : chunk));
    },
    onError: () => {
      toast({
        title: 'Microphone unavailable',
        description: 'Allow microphone access in your browser to dictate messages.',
        status: 'warning',
        duration: 4000,
      });
    },
  });

  // What the textarea shows: committed draft plus the live not-yet-final phrase.
  const displayValue =
    dictation.isListening && dictation.interimText
      ? `${draft}${draft ? ' ' : ''}${dictation.interimText}`
      : draft;

  const inputBg = useColorModeValue('gray.50', 'gray.700');
  const inputBorder = useColorModeValue('gray.200', 'gray.600');
  const placeholderColor = useColorModeValue('gray.400', 'gray.500');
  const emptyHeadingColor = useColorModeValue('gray.700', 'gray.100');
  const emptySubColor = useColorModeValue('gray.500', 'gray.400');
  const chipBorder = useColorModeValue('gray.200', 'gray.600');
  const chipHoverBg = useColorModeValue('gray.50', 'gray.700');
  const composerBg = useColorModeValue('white', 'gray.800');
  const dropOverlayBg = useColorModeValue('rgba(255,255,255,0.85)', 'rgba(26,32,44,0.85)');
  const dropAccent = useColorModeValue('purple.500', 'purple.300');

  // Auto-scroll on new content
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isResponding]);

  const showTyping = useMemo(() => {
    if (!isResponding) return false;
    const last = messages[messages.length - 1];
    return !(last && last.type === 'activity' && last.status === 'running');
  }, [isResponding, messages]);

  const handleSubmit = () => {
    // Capture the live interim phrase too, then discard pending recognition
    // results so a late final can't repopulate the cleared draft.
    const text = displayValue.trim();
    if (dictation.isListening) dictation.abort();
    const readyAttachments = attachments.filter((a) => a.status === 'done');
    if ((!text && readyAttachments.length === 0) || isResponding || isUploading) return;
    setDraft('');
    clearAttachments();
    onSend(text, readyAttachments);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleInput = (e) => {
    // Manual typing takes over: end dictation and keep whatever is visible
    // (the interim phrase is part of the field value the user is editing).
    if (dictation.isListening) dictation.abort();
    setDraft(e.target.value);
  };

  // Autosize up to ~5 rows (covers typing, dictation, and programmatic clears).
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
  }, [displayValue]);

  const handlePaste = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (files.length > 0) {
      e.preventDefault();
      addFiles(files);
    }
  };

  // Drag-and-drop anywhere on the chat surface. A depth counter keeps the
  // overlay stable while the drag passes over child elements.
  const handleDragEnter = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    setIsDragActive(true);
  };
  const handleDragOver = (e) => {
    if (dragHasFiles(e)) e.preventDefault();
  };
  const handleDragLeave = (e) => {
    if (!dragHasFiles(e)) return;
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) setIsDragActive(false);
  };
  const handleDrop = (e) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragDepthRef.current = 0;
    setIsDragActive(false);
    addFiles(e.dataTransfer.files);
  };

  const isEmpty = messages.length === 0;
  const canSubmit =
    (displayValue.trim().length > 0 || attachments.some((a) => a.status === 'done')) && !isUploading;

  return (
    <Flex
      direction="column"
      flex={1}
      minH={0}
      w="100%"
      position="relative"
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drop-target overlay */}
      {isDragActive && (
        <Flex
          position="absolute"
          inset={0}
          zIndex={10}
          bg={dropOverlayBg}
          align="center"
          justify="center"
          pointerEvents="none"
          border="2px dashed"
          borderColor={dropAccent}
          borderRadius="xl"
          m={2}
        >
          <VStack spacing={2}>
            <Icon as={FiPaperclip} boxSize={7} color={dropAccent} />
            <Text fontWeight="semibold" color={dropAccent}>
              Drop files to attach
            </Text>
            <Text fontSize="xs" color={dropAccent} opacity={0.8}>
              PDFs and images
            </Text>
          </VStack>
        </Flex>
      )}

      {/* Message list */}
      <Box ref={listRef} flex={1} overflowY="auto" px={4} py={4}>
        {isEmpty ? (
          <VStack spacing={4} justify="center" h="100%" px={4} textAlign="center">
            <Image src={astronautIcon} boxSize="64px" alt="FutureBot" />
            <Box>
              <Text fontWeight="bold" fontSize="lg" color={emptyHeadingColor}>
                Hey, I'm FutureBot
              </Text>
              <Text fontSize="sm" color={emptySubColor} mt={1}>
                I can see you're on <b>{pageTitle}</b>. Ask me anything about the Hub — or put
                me to work.
              </Text>
            </Box>
            <VStack spacing={2} w="100%">
              {(suggestions || []).map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant="outline"
                  borderColor={chipBorder}
                  _hover={{ bg: chipHoverBg }}
                  fontWeight="normal"
                  w="100%"
                  whiteSpace="normal"
                  h="auto"
                  py={2}
                  leftIcon={<Icon as={FiZap} boxSize={3.5} color="purple.400" />}
                  onClick={() => onSend(s)}
                >
                  {s}
                </Button>
              ))}
            </VStack>
          </VStack>
        ) : (
          <VStack spacing={3} align="stretch">
            {messages.map((msg) =>
              msg.type === 'activity' ? (
                <ActivityChip key={msg.id} message={msg} />
              ) : (
                <MessageBubble key={msg.id} message={msg} onSpeak={onSpeak} />
              )
            )}
            {showTyping && (
              <Flex justify="flex-start" gap={2} align="center">
                <Image src={astronautIcon} boxSize="24px" borderRadius="full" alt="" />
                <TypingIndicator />
              </Flex>
            )}
          </VStack>
        )}
      </Box>

      {/* Composer */}
      <Box px={3} py={3} bg={composerBg} borderTop="1px solid" borderColor={inputBorder}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="application/pdf,image/*"
          style={{ display: 'none' }}
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = '';
          }}
        />

        {attachments.length > 0 && (
          <Flex wrap="wrap" gap={2} mb={2}>
            {attachments.map((att) => (
              <PendingAttachmentChip key={att.id} att={att} onRemove={() => removeAttachment(att.id)} />
            ))}
          </Flex>
        )}

        <Flex align="flex-end" gap={1.5}>
          <IconButton
            icon={<FiPaperclip />}
            aria-label="Attach a PDF or image"
            onClick={() => fileInputRef.current?.click()}
            variant="ghost"
            borderRadius="full"
            size="md"
          />
          <Textarea
            ref={inputRef}
            value={displayValue}
            onChange={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={dictation.isListening ? 'Listening...' : `Ask about ${pageTitle}...`}
            rows={1}
            resize="none"
            minH="42px"
            maxH="120px"
            bg={inputBg}
            border="1px solid"
            borderColor={dictation.isListening ? 'red.300' : inputBorder}
            borderRadius="xl"
            fontSize="sm"
            _placeholder={{ color: placeholderColor }}
            _focus={{ borderColor: 'purple.400', boxShadow: '0 0 0 1px var(--chakra-colors-purple-400)' }}
          />
          {dictation.supported && (
            <IconButton
              icon={<FiMic />}
              aria-label={dictation.isListening ? 'Stop dictation' : 'Dictate a message'}
              onClick={dictation.toggle}
              variant={dictation.isListening ? 'solid' : 'ghost'}
              colorScheme={dictation.isListening ? 'red' : 'gray'}
              borderRadius="full"
              size="md"
              animation={dictation.isListening ? `${micPulse} 1.4s ease-out infinite` : undefined}
            />
          )}
          {isResponding ? (
            <IconButton
              icon={<FiSquare />}
              aria-label="Stop"
              onClick={onStop}
              borderRadius="full"
              colorScheme="gray"
              size="md"
            />
          ) : (
            <IconButton
              icon={<FiArrowUp />}
              aria-label="Send"
              onClick={handleSubmit}
              isDisabled={!canSubmit}
              borderRadius="full"
              colorScheme="purple"
              size="md"
            />
          )}
        </Flex>
      </Box>
    </Flex>
  );
}
